import React, { useState, useEffect } from 'react'
import Page from '../../components/Page'
import { Users, TrendingUp, MapPin, Building2, Star, DollarSign, AlertCircle, Lightbulb, CheckCircle2, Zap, AlertTriangle, X, Search, ChevronLeft, ChevronRight } from 'lucide-react'
import KPICard from '../../components/KPICard'
import ContentCard from '../../components/ContentCard'
import StatCard from '../../components/StatCard'
import AlertCard from '../../components/AlertCard'
import { useGetClientStatisticsQuery } from '../../services/api'
import { useOwner } from '../../contexts/OwnerContext'
import { AIAssistantButton } from '../../components/AIAssistant'

const ClientsOverviewPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { isOwnerValid, isLoading: ownerLoading } = useOwner()

  // Modal states
  const [selectedInsight, setSelectedInsight] = useState<any | null>(null)
  const [insightClients, setInsightClients] = useState<any[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(5)

  // Buscar dados da API (só faz requisição se owner estiver válido)
  // Usando apenas statistics pois já retorna todos os dados necessários (incluindo portfolio_analysis)
  const { data: statisticsData, error: statisticsError, isLoading: statsLoading } = useGetClientStatisticsQuery(undefined, {
    skip: !isOwnerValid || ownerLoading,
    refetchOnMountOrArgChange: false, // Usar cache para evitar requisições desnecessárias
    refetchOnFocus: false,
    refetchOnReconnect: false,
  })

  useEffect(() => {
    if (!statsLoading) {
      setIsLoading(false)
    }

    if (statisticsError) {
      // Extrair mensagem de erro mais detalhada
      const error = statisticsError
      let errorMessage = 'Erro ao carregar dados dos clientes'
      
      if (error && 'data' in error) {
        const errorData = error.data as any
        if (errorData?.detail) {
          errorMessage = errorData.detail
        } else if (errorData?.message) {
          errorMessage = errorData.message
        } else if (typeof errorData === 'string') {
          errorMessage = errorData
        }
      } else if (error && 'message' in error) {
        errorMessage = (error as any).message
      }
      
      // Log para debug (apenas em desenvolvimento)
      if ((import.meta as any).env?.DEV) {
        console.error('❌ Erro ao carregar dados de clientes:', {
          statisticsError,
          errorMessage
        })
      }
      
      setError(errorMessage)
    } else {
      // Limpar erro se os dados foram carregados com sucesso
      if (statisticsData) {
        setError(null)
      }
    }
  }, [statisticsData, statisticsError, statsLoading])

  // Função para processar clientes baseado no tipo de insight
  const handleInsightClick = (insight: any) => {
    // Os clientes estão dentro de analysis.clients (retornado pelo backend)
    const clients = statisticsData?.analysis?.clients || []
    const analysis = statisticsData?.analysis || {}

    let filteredClients: any[] = []

    if (insight.title?.includes('Premium') || insight.title?.includes('Elite') || insight.title?.includes('Foco em Clientes Premium')) {
      // Usar clientes do tier Gold gerados pela IA
      const allClientsByTier = analysis.segmentation?.all_clients_per_tier || {}
      const goldClients = allClientsByTier['Gold'] || []

      filteredClients = goldClients
    } else if (insight.title?.includes('Reativação')) {
      // Mostrar clientes inativos
      filteredClients = clients.filter((c: any) => {
        const status = (c.statusCliente || '').toUpperCase()
        return status === 'INATIVO' || status === 'INATIVE'
      })
    } else if (insight.title?.includes('Alto Potencial')) {
      // Mostrar clientes com alto potencial
      filteredClients = clients.filter((c: any) => {
        const potential = (c.potencialCrescimento || '').toUpperCase()
        return potential === 'ALTO' || potential === 'HIGH'
      })
    } else if (insight.title?.includes('Expansão Regional')) {
      // Mostrar clientes dos estados com menor representação
      const byState = analysis.geographic_analysis?.by_state || {}
      const allStates = Object.keys(byState)

      if (allStates.length > 0) {
        const minState = allStates.reduce((min, state) => byState[min] < byState[state] ? min : state)
        filteredClients = clients.filter((c: any) =>
          c.estado && c.estado.toUpperCase() !== minState?.toUpperCase()
        )
      }
    } else if (insight.title?.includes('Concentração Geográfica')) {
      // Mostrar clientes do estado com maior concentração
      const byState = analysis.geographic_analysis?.by_state || {}

      if (Object.keys(byState).length > 0) {
        const maxState = Object.keys(byState).reduce((max, state) =>
          byState[max] > byState[state] ? max : state
        )
        filteredClients = clients.filter((c: any) =>
          c.estado && c.estado.toUpperCase() === maxState?.toUpperCase()
        )
      }
    } else {
      // Default: mostrar todos os clientes
      filteredClients = clients
    }

    // Ordenar por nome (criar cópia para evitar erro em arrays imutáveis do Redux)
    filteredClients = [...filteredClients].sort((a: any, b: any) =>
      (a.descricaoCliente || a.nome || '').localeCompare(b.descricaoCliente || b.nome || '')
    )

    setInsightClients(filteredClients)
    setSelectedInsight(insight)
    setSearchTerm('')
    setCurrentPage(1)
  }

  // Filtrar clientes por termo de busca
  const filteredInsightClients = insightClients.filter(c =>
    c.descricaoCliente?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.codigoCliente?.includes(searchTerm)
  )

  // Paginação
  const totalPages = Math.ceil(filteredInsightClients.length / itemsPerPage)
  const paginatedClients = filteredInsightClients.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  if (isLoading) {
    return (
      <Page
        title="👥 Visão Geral - Carteira de Clientes"
        subtitle="Carregando dados dos clientes..."
        onRefresh={() => window.location.reload()}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--primary)' }}></div>
            <p style={{ color: 'var(--text-secondary)' }}>Carregando dados dos clientes...</p>
            <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
              Loading: {statsLoading ? 'Sim' : 'Não'}
            </p>
          </div>
        </div>
      </Page>
    )
  }

  if (error) {
    return (
      <Page
        title="👥 Visão Geral - Carteira de Clientes"
        subtitle="Erro ao carregar dados"
        onRefresh={() => window.location.reload()}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center max-w-md">
            <AlertCircle className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--error)' }} />
            <p className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Erro ao carregar dados dos clientes
            </p>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              {error}
            </p>
            {(import.meta as any).env?.DEV && statisticsError && (
              <div className="mt-4 p-4 rounded-lg text-left text-xs" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                <p className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Detalhes técnicos (dev):</p>
                <pre className="overflow-auto" style={{ color: 'var(--text-secondary)' }}>
                  {JSON.stringify({ statisticsError }, null, 2)}
                </pre>
              </div>
            )}
            <div className="mt-6 space-y-2">
              <button
                onClick={() => {
                  setError(null)
                  setIsLoading(true)
                  window.location.reload()
                }}
                className="px-6 py-3 rounded-lg text-white font-semibold transition-all hover:opacity-90"
                style={{ backgroundColor: 'var(--primary)' }}
              >
                Tentar Novamente
              </button>
              {!isOwnerValid && (
                <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                  ⚠️ Verifique se você está autenticado corretamente
                </p>
              )}
            </div>
          </div>
        </div>
      </Page>
    )
  }

  // Processar dados da API
  const totalClients = statisticsData?.total_clients || 0
  const activeClients = statisticsData?.active_clients || 0
  const inactiveClients = statisticsData?.inactive_clients || 0
  const classificationDistribution = statisticsData?.classification_distribution || {}
  const stateDistribution = statisticsData?.state_distribution || {}
  const analysis = statisticsData?.analysis || {}

  // Calcular KPIs baseados em dados REAIS (não sintéticos)
  // Dados de receita vêm do portfolio_analysis (que são dados reais ou indicam indisponibilidade)
  const portfolioAnalysis = analysis.portfolio_analysis || {}
  const totalRevenue = portfolioAnalysis.data_available ? (portfolioAnalysis.total_value || 0) : 0
  const averageTicket = portfolioAnalysis.data_available ? (portfolioAnalysis.average_ticket || 0) : 0
  const averageValue = portfolioAnalysis.data_available ? (portfolioAnalysis.average_value || 0) : 0
  const hasPurchaseData = portfolioAnalysis.data_available === true

  const activePercentage = totalClients > 0 ? ((activeClients / totalClients) * 100).toFixed(1) : '0'
  const segmentationTiers = analysis.segmentation?.by_tier || {}

  const kpis = [
    {
      label: 'Total de Clientes',
      value: totalClients.toLocaleString().replace(/,/g, '.'),
      change: {
        value: `${activePercentage}% ativos`,
        type: 'increase' as const
      },
      icon: Users,
      color: 'blue' as const
    },
    {
      label: 'Receita Total',
      value: hasPurchaseData ? `R$ ${(totalRevenue / 1000).toFixed(1)}k` : 'Não disponível',
      change: {
        value: hasPurchaseData
          ? `Ticket: R$ ${averageTicket.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`
          : 'Aguardando dados de compra',
        type: hasPurchaseData ? 'increase' as const : 'neutral' as const
      },
      icon: DollarSign,
      color: hasPurchaseData ? 'green' as const : 'indigo' as const
    },
    {
      label: 'Valor Médio/Cliente',
      value: hasPurchaseData ? `R$ ${(averageValue / 1000).toFixed(1)}k` : 'Não disponível',
      change: {
        value: hasPurchaseData
          ? 'Baseado em dados reais'
          : 'Sem dados de compra na carteira',
        type: 'increase' as const
      },
      icon: TrendingUp,
      color: hasPurchaseData ? 'purple' as const : 'indigo' as const
    },
    {
      label: 'Cobertura Geográfica',
      value: `${analysis.geographic_analysis?.total_states || 0} estados`,
      change: {
        value: `${analysis.geographic_analysis?.total_cities || 0} cidades`,
        type: 'increase' as const
      },
      icon: MapPin,
      color: 'orange' as const
    }
  ]

  // Processar distribuição geográfica
  const geographicData = Object.entries(stateDistribution).map(([state, count]) => ({
    state,
    clients: count as number
  })).slice(0, 5)

  // Processar segmentação
  const segmentData = Object.entries(classificationDistribution).map(([classification, count]) => ({
    segment: `Classe ${classification}`,
    count: count as number,
    percentage: ((count as number) / totalClients * 100).toFixed(1)
  }))

  // Gerar alertas baseados nos dados
  const alerts = [
    {
      type: 'success' as const,
      title: 'Dados carregados com sucesso',
      message: `${totalClients} clientes encontrados na base de dados`
    },
    ...(activeClients < totalClients ? [{
      type: 'warning' as const,
      title: 'Clientes inativos',
      message: `${inactiveClients} clientes estão inativos`
    }] : []),
    ...(analysis.strategic_insights?.length > 0 ? [{
      type: 'info' as const,
      title: 'Insights disponíveis',
      message: `${analysis.strategic_insights.length} insights estratégicos gerados`
    }] : [])
  ]

  return (
    <>
      <Page
        title="👥 Visão Geral - Carteira de Clientes"
        subtitle="Análise completa da carteira de clientes da empresa"
        onRefresh={() => window.location.reload()}
      >
      <div className="space-y-8">
        {/* AI Assistant Button - Show when data is loaded */}
        {!isLoading && statisticsData && (() => {
          // Contexto normalizado para Clientes
          const screenContext = 'Clients'

          return <AIAssistantButton screenContext={screenContext} />
        })()}

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {kpis.map((kpi, index) => (
            <KPICard
              key={index}
              label={kpi.label}
              value={kpi.value}
              change={kpi.change}
              icon={kpi.icon}
              color={kpi.color}
            />
          ))}
        </div>

               {/* Segmentação por Tiers */}
               {Object.keys(segmentationTiers).length > 0 && (
                 <ContentCard
                   title="Segmentação de Clientes por Tier"
                   subtitle="Classificação por potencial e valor"
                   icon={Star}
                 >
                   <div className="space-y-6">
                     {Object.entries(segmentationTiers).map(([tier, count]: any) => {
                       const tierColors = {
                         'Gold': { bg: '#FCD34D', border: '#F59E0B', percentage: (count / totalClients * 100).toFixed(1) },
                         'Silver': { bg: '#E5E7EB', border: '#9CA3AF', percentage: (count / totalClients * 100).toFixed(1) },
                         'Bronze': { bg: '#FEF3C7', border: '#D97706', percentage: (count / totalClients * 100).toFixed(1) }
                       }
                       const colors = tierColors[tier as keyof typeof tierColors] || { bg: '#F3F4F6', border: '#D1D5DB', percentage: '0' }

                       return (
                         <div key={tier} className="space-y-2">
                           <div className="flex items-center justify-between">
                             <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                               {tier === 'Gold' ? '👑' : tier === 'Silver' ? '🥈' : '🥉'} {tier}
                             </span>
                             <div className="flex items-center space-x-2">
                               <span className="text-sm font-medium">{count} clientes</span>
                               <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>({colors.percentage}%)</span>
                             </div>
                           </div>
                           <div
                             className="h-3 rounded-full"
                             style={{ backgroundColor: colors.bg, border: `2px solid ${colors.border}` }}
                           >
                             <div
                               className="h-full rounded-full transition-all duration-500"
                               style={{
                                 backgroundColor: colors.border,
                                 width: `${colors.percentage}%`
                               }}
                             />
                           </div>
                         </div>
                       )
                     })}
                   </div>
                 </ContentCard>
               )}

               {/* Dados de Compras: Removido */}
               {/*
                 NOTA: A seção "Top Clientes por Valor" foi removida.
                 Anteriormente exibia dados sintéticos (fictícios) de compras.

                 A análise agora utiliza APENAS dados reais:
                 - Classificação de Clientes
                 - Status (Ativo/Inativo)
                 - Potencial de Crescimento
                 - Análise Geográfica
                 - Limites de Crédito

                 Quando dados reais de compras estiverem disponíveis na carteira,
                 eles serão utilizados automaticamente.
               */}

        {/* Gráficos */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Distribuição Geográfica */}
          <ContentCard
            title="Distribuição Geográfica"
            subtitle="Clientes por estado"
            icon={MapPin}
          >
            <div className="space-y-4">
              {geographicData.length > 0 ? geographicData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    {item.state}
                  </span>
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-32 h-2 rounded-full"
                      style={{ backgroundColor: 'var(--bg-tertiary)' }}
                    >
                      <div
                        className="h-2 rounded-full transition-all duration-500"
                        style={{
                          backgroundColor: 'var(--primary)',
                          width: `${Math.max(10, (item.clients / Math.max(...geographicData.map(d => d.clients))) * 100)}%`
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium w-12 text-right" style={{ color: 'var(--text-primary)' }}>
                      {item.clients}
                    </span>
                  </div>
                </div>
              )) : (
                <div className="text-center py-4">
                  <p style={{ color: 'var(--text-secondary)' }}>Nenhum dado geográfico disponível</p>
                </div>
              )}
            </div>
          </ContentCard>

          {/* Segmentação de Clientes */}
          <ContentCard
            title="Segmentação de Clientes"
            subtitle="Distribuição por classificação"
            icon={TrendingUp}
          >
            <div className="space-y-4">
              {segmentData.length > 0 ? segmentData.map((segment, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: `hsl(${index * 60}, 70%, 50%)` }}
                    />
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {segment.segment}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium w-16 text-right" style={{ color: 'var(--text-primary)' }}>
                      {segment.count}
                    </span>
                    <span className="text-sm font-medium w-12 text-right" style={{ color: 'var(--text-secondary)' }}>
                      {segment.percentage}%
                    </span>
                  </div>
                </div>
              )) : (
                <div className="text-center py-4">
                  <p style={{ color: 'var(--text-secondary)' }}>Nenhum dado de segmentação disponível</p>
                </div>
              )}
            </div>
          </ContentCard>
        </div>

               {/* Estatísticas Adicionais */}
               <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                 <StatCard
                   label="Cidades Atendidas"
                   value={`${analysis.geographic_analysis?.total_cities || 0} cidades`}
                   icon={MapPin}
                   color="blue"
                 />
                 <StatCard
                   label="Taxa de Ativos"
                   value={`${activePercentage}%`}
                   icon={TrendingUp}
                   color="green"
                 />
                 <StatCard
                   label="Regionais"
                   value={`${analysis.geographic_analysis?.total_regionals || 0} regionais`}
                   icon={Building2}
                   color="purple"
                 />
               </div>

        {/* Alertas */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
            Alertas e Notificações
          </h2>
          <div className="space-y-3">
            {alerts.map((alert, index) => (
              <AlertCard
                key={index}
                type={alert.type}
                title={alert.title}
                message={alert.message}
              />
            ))}
          </div>
        </div>

        {/* Recomendações */}
        {/* {analysis.recommendations?.length > 0 && (
          <ContentCard
            title="Recomendações Acionáveis"
            subtitle="Ações recomendadas para melhoria"
            icon={TrendingUp}
          >
            <div className="space-y-3">
              {analysis.recommendations.map((rec: any, index: number) => (
                <div
                  key={index}
                  className="p-3 rounded-lg flex items-start space-x-3"
                  style={{ backgroundColor: 'var(--bg-secondary)' }}
                >
                  <div className="text-xl mt-1">👉</div>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    {rec}
                  </p>
                </div>
              ))}
            </div>
          </ContentCard>
        )} */}

        {/* Insights Estratégicos - Cards Elegantes */}
        {analysis.strategic_insights?.length > 0 && (
          <ContentCard
            title="💡 Insights Estratégicos"
            subtitle="Análises e observações estratégicas baseadas em dados reais"
            icon={Lightbulb}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {analysis.strategic_insights.map((insight: any, index: number) => {
                // Suportar tanto formato antigo (string) quanto novo (objeto com type, title, insight)
                const isObject = typeof insight === 'object' && insight !== null
                const insightText = isObject ? insight.insight : insight
                const insightTitle = isObject ? insight.title : null
                const insightType = isObject ? insight.type : 'insight'

                // Configuração por tipo de insight
                const typeConfig = {
                  'opportunity': {
                    borderColor: '#10B981',
                    bgColor: 'rgba(16, 185, 129, 0.1)',
                    icon: Zap,
                    label: '✨ Oportunidade',
                    textColor: '#10B981'
                  },
                  'warning': {
                    borderColor: '#F59E0B',
                    bgColor: 'rgba(245, 158, 11, 0.1)',
                    icon: AlertTriangle,
                    label: '⚠️ Atenção',
                    textColor: '#F59E0B'
                  },
                  'recommendation': {
                    borderColor: '#3B82F6',
                    bgColor: 'rgba(59, 130, 246, 0.1)',
                    icon: CheckCircle2,
                    label: '💡 Recomendação',
                    textColor: '#3B82F6'
                  },
                  'insight': {
                    borderColor: 'var(--primary)',
                    bgColor: 'rgba(79, 70, 229, 0.1)',
                    icon: Lightbulb,
                    label: '📊 Insight',
                    textColor: 'var(--primary)'
                  }
                }

                const config = typeConfig[insightType as keyof typeof typeConfig] || typeConfig['insight']
                const IconComponent = config.icon

                return (
                  <div
                    key={index}
                    onClick={() => handleInsightClick(insight)}
                    className="relative rounded-xl overflow-hidden border-2 cursor-pointer"
                    style={{
                      backgroundColor: 'var(--bg-secondary)',
                      borderColor: config.borderColor,
                      minHeight: '200px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {/* Ícone de Background */}
                    <div
                      className="absolute top-0 right-0 w-20 h-20 opacity-10 -mr-5 -mt-5"
                      style={{ color: config.textColor }}
                    >
                      <IconComponent className="w-full h-full" />
                    </div>

                    {/* Conteúdo */}
                    <div className="p-4 relative z-10 flex-1 flex flex-col">
                      {/* Header */}
                      <div className="flex items-center gap-2 mb-3">
                        <div
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: config.bgColor }}
                        >
                          <IconComponent
                            className="w-4 h-4"
                            style={{ color: config.textColor }}
                          />
                        </div>
                        <span
                          className="text-xs font-bold uppercase tracking-wide"
                          style={{ color: config.textColor }}
                        >
                          {config.label}
                        </span>
                      </div>

                      {/* Título */}
                      {insightTitle && (
                        <h3
                          className="text-sm font-bold mb-2 line-clamp-2"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {insightTitle}
                        </h3>
                      )}

                      {/* Descrição */}
                      <p
                        className="text-xs leading-relaxed flex-1"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        {insightText}
                      </p>

                      {/* Footer com badge acionável */}
                      {isObject && insightType !== 'insight' && (
                        <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--border-primary)' }}>
                          <div
                            className="inline-block px-2 py-1 rounded text-xs font-semibold"
                            style={{
                              backgroundColor: config.bgColor,
                              color: config.textColor
                            }}
                          >
                            Ação recomendada
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </ContentCard>
        )}

        {/* Recomendações Acionáveis */}
        {analysis.recommendations?.length > 0 && (
          <ContentCard
            title="🎯 Recomendações Acionáveis"
            subtitle="Ações prioritárias para otimizar a carteira de clientes"
            icon={CheckCircle2}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {analysis.recommendations.map((recommendation: any, index: number) => {
                // Suportar ambos formatos (string e objeto)
                const isObject = typeof recommendation === 'object' && recommendation !== null
                const recText = isObject ? recommendation.text || recommendation.description || recommendation.recommendation : recommendation

                // Cores alternadas para cada recomendação
                const colors = [
                  { border: '#3B82F6', bg: 'rgba(59, 130, 246, 0.1)', number: '🎯' },
                  { border: '#10B981', bg: 'rgba(16, 185, 129, 0.1)', number: '📈' },
                  { border: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)', number: '⚡' },
                  { border: '#EC4899', bg: 'rgba(236, 72, 153, 0.1)', number: '💎' },
                  { border: '#8B5CF6', bg: 'rgba(139, 92, 246, 0.1)', number: '🚀' },
                  { border: '#14B8A6', bg: 'rgba(20, 184, 166, 0.1)', number: '✨' }
                ]

                const colorIndex = index % colors.length
                const color = colors[colorIndex]

                return (
                  <div
                    key={index}
                    className="relative rounded-xl overflow-hidden border-2"
                    style={{
                      backgroundColor: 'var(--bg-secondary)',
                      borderColor: color.border,
                      minHeight: '160px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {/* Número / Indicador */}
                    <div
                      className="absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold"
                      style={{ backgroundColor: color.bg, color: color.border }}
                    >
                      {index + 1}
                    </div>

                    {/* Conteúdo */}
                    <div className="p-4 pr-16 flex flex-col h-full justify-between">
                      {/* Emoji/Label */}
                      <div className="text-2xl mb-2">{color.number}</div>

                      {/* Texto da recomendação */}
                      <p
                        className="text-sm leading-relaxed font-medium flex-1"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {recText}
                      </p>

                      {/* Footer */}
                      <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--border-primary)' }}>
                        <p
                          className="text-xs font-semibold"
                          style={{ color: color.border }}
                        >
                          → Iniciar implementação
                        </p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Dica de Priorização */}
            <div
              className="mt-6 p-4 rounded-lg border-l-4"
              style={{
                backgroundColor: 'rgba(59, 130, 246, 0.05)',
                borderColor: '#3B82F6'
              }}
            >
              <p
                className="text-sm"
                style={{ color: 'var(--text-secondary)' }}
              >
                <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  💡 Dica:
                </span>
                {' '} Priorize as recomendações que têm maior impacto no crescimento da sua carteira de clientes. Implemente uma por vez e acompanhe os resultados.
              </p>
            </div>
          </ContentCard>
        )}
      </div>
      </Page>

      {/* Modal de Detalhes do Insight - Moderna e Elegante */}
      {selectedInsight && (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
        onClick={() => setSelectedInsight(null)}
      >
        <div
          className="rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col transform transition-all"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header Modal com Azul Premium */}
          <div
            className="px-10 py-8 flex items-center justify-between relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #1E40AF 0%, #1E3A8A 50%, #1E40AF 100%)',
              boxShadow: '0 10px 30px rgba(30, 64, 175, 0.3)'
            }}
          >
            {/* Efeito de background decorativo */}
            <div
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage: 'radial-gradient(circle at 20% 50%, white 0%, transparent 50%)',
                backgroundSize: '200% 200%'
              }}
            />

            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-1 h-8 rounded-full" style={{ backgroundColor: '#FBBF24' }} />
                <h2 className="text-3xl font-bold text-white">
                  {selectedInsight.title}
                </h2>
              </div>
              <p className="text-white text-opacity-90 text-sm font-medium">
                📊 {filteredInsightClients.length} cliente{filteredInsightClients.length !== 1 ? 's' : ''} encontrado{filteredInsightClients.length !== 1 ? 's' : ''}
              </p>
            </div>

            <button
              onClick={() => setSelectedInsight(null)}
              className="p-3 rounded-xl transition-all duration-200 relative z-10 hover:bg-white hover:bg-opacity-20"
              style={{ color: 'white' }}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Barra de Busca - Moderna */}
          <div className="px-10 py-6" style={{ backgroundColor: 'var(--bg-secondary)' }}>
            <div className="relative group">
              <Search
                className="absolute left-4 top-3.5 w-5 h-5 transition-colors group-focus-within:text-indigo-500"
                style={{ color: 'var(--text-secondary)' }}
              />
              <input
                type="text"
                placeholder="Buscar por nome ou código do cliente..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setCurrentPage(1)
                }}
                className="w-full pl-12 pr-6 py-3 rounded-xl border-2 transition-all duration-200 font-medium text-sm"
                style={{
                  borderColor: searchTerm ? '#1E40AF' : 'var(--border-primary)',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  boxShadow: searchTerm ? '0 4px 15px rgba(30, 64, 175, 0.1)' : 'none'
                }}
              />
            </div>
          </div>

          {/* Tabela Elegante e Moderna */}
          <div className="flex-1 overflow-auto px-10 py-6">
            {paginatedClients.length > 0 ? (
              <div className="overflow-hidden rounded-2xl border" style={{ borderColor: 'var(--border-primary)' }}>
                <table className="w-full">
                  <thead>
                    <tr
                      style={{
                        background: 'linear-gradient(90deg, #1E40AF 0%, #1E3A8A 100%)',
                        color: 'white',
                        borderBottom: '2px solid #1E40AF'
                      }}
                    >
                      <th className="text-left py-4 px-6 font-bold text-sm uppercase tracking-wider text-white">
                        Código
                      </th>
                      <th className="text-left py-4 px-6 font-bold text-sm uppercase tracking-wider text-white">
                        Cliente
                      </th>
                      <th className="text-left py-4 px-6 font-bold text-sm uppercase tracking-wider text-white">
                        Localização
                      </th>
                      <th className="text-left py-4 px-6 font-bold text-sm uppercase tracking-wider text-white">
                        Classificação
                      </th>
                      <th className="text-left py-4 px-6 font-bold text-sm uppercase tracking-wider text-white">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedClients.map((client: any, idx: number) => (
                      <tr
                        key={idx}
                        style={{
                          borderBottom: '1px solid var(--border-primary)',
                          backgroundColor: idx % 2 === 0
                            ? 'var(--bg-secondary)'
                            : 'rgba(30, 64, 175, 0.02)'
                        }}
                      >
                        <td className="py-4 px-6 text-sm font-mono font-bold" style={{ color: '#1E40AF' }}>
                          {client.codigoCliente}
                        </td>
                        <td className="py-4 px-6 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {client.descricaoCliente || client.nome}
                        </td>
                        <td className="py-4 px-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" style={{ color: '#10B981' }} />
                            {client.cidade}, {client.estado}
                          </div>
                        </td>
                        <td className="py-4 px-6 text-sm">
                          <span
                            className="px-4 py-1.5 rounded-lg text-xs font-bold inline-flex items-center gap-1 transition-all"
                            style={{
                              backgroundColor: 'rgba(30, 64, 175, 0.15)',
                              color: '#1E40AF',
                              border: '1px solid rgba(30, 64, 175, 0.3)'
                            }}
                          >
                            ⭐ {client.classificacaoCliente || client.classification || 'N/A'}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-sm">
                          <span
                            className="px-4 py-1.5 rounded-lg text-xs font-bold inline-flex items-center gap-1 transition-all"
                            style={{
                              backgroundColor: client.statusCliente === 'ATIVO' || client.status === 'ACTIVE'
                                ? 'rgba(16, 185, 129, 0.15)'
                                : 'rgba(245, 158, 11, 0.15)',
                              color: client.statusCliente === 'ATIVO' || client.status === 'ACTIVE'
                                ? '#10B981'
                                : '#F59E0B',
                              border: client.statusCliente === 'ATIVO' || client.status === 'ACTIVE'
                                ? '1px solid rgba(16, 185, 129, 0.3)'
                                : '1px solid rgba(245, 158, 11, 0.3)'
                            }}
                          >
                            {client.statusCliente === 'ATIVO' || client.status === 'ACTIVE' ? '🟢 Ativo' : '🟠 Inativo'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                  style={{ backgroundColor: 'rgba(30, 64, 175, 0.1)' }}
                >
                  <Users className="w-8 h-8" style={{ color: 'var(--text-secondary)' }} />
                </div>
                <p className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                  Nenhum cliente encontrado
                </p>
                <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
                  Refine sua busca ou tente outro termo
                </p>
              </div>
            )}
          </div>

          {/* Paginação Moderna */}
          {filteredInsightClients.length > 0 && (
            <div
              className="px-10 py-6 border-t flex items-center justify-between backdrop-blur-sm"
              style={{
                borderColor: 'var(--border-primary)',
                backgroundColor: 'rgba(30, 64, 175, 0.02)'
              }}
            >
              <div className="flex items-center gap-6">
                {/* Seletor de Registros por Página */}
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                    Registros por página:
                  </label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value))
                      setCurrentPage(1)
                    }}
                    className="px-4 py-2 rounded-lg border-2 transition-all duration-200 font-bold text-sm hover:shadow-md"
                    style={{
                      borderColor: '#1E40AF',
                      backgroundColor: 'var(--bg-secondary)',
                      color: '#1E40AF',
                      cursor: 'pointer',
                      appearance: 'none',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%231E40AF' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 8px center',
                      paddingRight: '28px'
                    }}
                  >
                    <option value={5}>5 registros</option>
                    <option value={10}>10 registros</option>
                    <option value={20}>20 registros</option>
                    <option value={50}>50 registros</option>
                  </select>
                </div>

                {/* Informações de Paginação */}
                <div className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                  <span style={{ color: '#1E40AF', fontWeight: 'bold' }}>Página {currentPage} de {totalPages}</span>
                  <span> • {Math.min(itemsPerPage, paginatedClients.length)} de {filteredInsightClients.length} clientes</span>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="p-2.5 rounded-lg border-2 transition-all duration-200 hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    borderColor: 'var(--border-primary)',
                    backgroundColor: 'var(--bg-secondary)',
                    color: currentPage === 1 ? 'var(--text-secondary)' : '#1E40AF'
                  }}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                {/* Números de página */}
                <div className="flex gap-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum = i + 1
                    if (totalPages > 5 && currentPage > 3) {
                      pageNum = currentPage - 2 + i
                    }
                    if (pageNum > totalPages) return null

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className="w-9 h-9 rounded-lg border-2 transition-all duration-200 font-bold text-sm hover:shadow-md"
                        style={{
                          borderColor: currentPage === pageNum ? '#1E40AF' : 'var(--border-primary)',
                          backgroundColor: currentPage === pageNum ? '#1E40AF' : 'var(--bg-secondary)',
                          color: currentPage === pageNum ? 'white' : 'var(--text-primary)'
                        }}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2.5 rounded-lg border-2 transition-all duration-200 hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    borderColor: 'var(--border-primary)',
                    backgroundColor: 'var(--bg-secondary)',
                    color: currentPage === totalPages ? 'var(--text-secondary)' : '#1E40AF'
                  }}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      )}
    </>
  )
}

export default ClientsOverviewPage